import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Shield, CheckCircle, AlertTriangle, ArrowUpRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { ServiceUptimeItem } from '@/modules/Service/services/sla'
import { generatePathDatacentral, paths } from '@/utils/path'
import { formatMinutes } from '../../Shared/utils/format-value'

interface ServicesTableProps {
    services: ServiceUptimeItem[]
    isLoading: boolean
    periodLabel: string
}

const ServicesTable = ({
    services,
    isLoading,
    periodLabel,
}: ServicesTableProps) => {
    const hasIncidents = useMemo(
        () => services.some((s) => (s.incident_count || 0) > 0),
        [services]
    )

    const columns = useMemo(
        (): DataTableColumn<ServiceUptimeItem>[] => [
            {
                key: 'service_name',
                title: 'Dịch vụ',
                render: (_val, row) => (
                    <span className="font-medium">{row.service_name}</span>
                ),
            },
            {
                key: 'customer_name',
                title: 'Khách hàng',
                render: (_val, row) => (
                    <span className="text-sm text-gray-500">
                        {row.customer_name}
                    </span>
                ),
            },
            {
                key: 'target_uptime',
                title: 'Target',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <span className="text-sm text-blue-600">
                        {row.target_uptime != null
                            ? `${row.target_uptime}%`
                            : '—'}
                    </span>
                ),
            },
            {
                key: 'actual_uptime',
                title: 'Actual',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <span
                        className={`text-sm font-semibold ${
                            row.sla_met ? 'text-green-600' : 'text-red-600'
                        }`}
                    >
                        {row.actual_uptime != null
                            ? `${row.actual_uptime.toFixed(2)}%`
                            : '—'}
                    </span>
                ),
            },
            {
                key: 'sla_downtime_minutes',
                title: 'Downtime',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <div>
                        <span className="text-sm text-orange-600">
                            {formatMinutes(row.sla_downtime_minutes)}
                        </span>
                        <span className="text-xs text-gray-400 block">
                            / {formatMinutes(row.allowed_downtime)}
                        </span>
                    </div>
                ),
            },
            ...(hasIncidents
                ? [
                      {
                          key: 'incident_count' as keyof ServiceUptimeItem,
                          title: 'Sự cố',
                          headerClassName: 'text-center',
                          cellClassName: 'text-center',
                          render: (_val: unknown, row: ServiceUptimeItem) =>
                              row.incident_count > 0 ? (
                                  <Badge
                                      variant="destructive"
                                      className="text-xs"
                                  >
                                      {row.incident_count}
                                  </Badge>
                              ) : (
                                  <span className="text-gray-400">—</span>
                              ),
                      },
                  ]
                : []),
            {
                key: 'sla_met',
                title: 'Trạng thái',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) =>
                    row.sla_met ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Đạt SLA
                        </Badge>
                    ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Vi phạm SLA
                        </Badge>
                    ),
            },
            {
                key: 'actions',
                title: '',
                width: 48,
                render: (_val, row) => (
                    <Link
                        to={generatePathDatacentral(paths.serviceSLAConfig, {
                            id: row.service_id,
                        })}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                        >
                            <ArrowUpRight className="w-4 h-4" />
                        </Button>
                    </Link>
                ),
            },
        ],
        [hasIncidents]
    )

    if (isLoading) {
        return <Skeleton className="h-64 w-full" />
    }

    return (
        <Paper>
            <div className="flex items-center justify-between px-4 pt-4 pb-3">
                <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-500" />
                    <p className="text-sm font-medium">
                        Dịch vụ &amp; Uptime · {periodLabel}
                    </p>
                </div>
                <Badge variant="outline">{services.length} dịch vụ</Badge>
            </div>
            <p className="text-sm text-gray-500 px-4 pb-3">
                Danh sách dịch vụ với thống kê uptime (vi phạm SLA đầu tiên)
            </p>
            <DataTableCustomized<ServiceUptimeItem>
                columns={columns}
                rows={services}
                rowKey={(row) => row.service_id}
                emptyMessage="Chưa có dữ liệu cho kỳ này. Hãy chọn kỳ khác hoặc kiểm tra lại sau."
                size="small"
                bordered
            />
        </Paper>
    )
}

export default ServicesTable
